import type { SLDDeviceState, SLDNodeType } from './sldTypes';

/**
 * Threshold beyond which a device with no recent telemetry flips into the
 * `no_comms` tier.
 *
 * This is a DEVICE-level judgement over per-device telemetry samples, which
 * arrive on the batch cadence (~5s) — it is not the site-level heartbeat
 * threshold and must not be re-synced to it. It was documented as mirroring
 * `OFFLINE_THRESHOLD_SECONDS=60`; that constant is now 120s and derived from
 * the heartbeat WRITE cadence (djin-cloud#718, backend/src/config/site-liveness.ts),
 * a cadence this value has nothing to do with. 60s remains ~12 missed samples
 * here, which is the intended meaning: "we haven't heard from this device in
 * over a minute".
 *
 * Issue: djin-cloud#96, corrected by djin-cloud#718.
 */
export const DEVICE_NO_COMMS_THRESHOLD_MS = 60_000;

/**
 * Power (kW) above which a generator counts as demonstrably producing, and so
 * is demoted out of the fault tier by `computeDeviceState`. Mirrors
 * `RUNNING_POWER_THRESHOLD_KW` in
 * web/src/components/site-detail/cards/GeneratorsCard.tsx — a running Hondo or
 * Turner generator sits in the hundreds of kW while an idling one sits near
 * zero, so 1 kW separates them cleanly. Strictly greater-than: exactly 1 kW is
 * not producing evidence.
 *
 * Issue: djin-cloud#622.
 */
export const GENERATOR_PRODUCING_THRESHOLD_KW = 1;

/**
 * Inputs to `computeDeviceState`. Optional everywhere — callers pass only
 * the subset relevant to their device type. Generator passes status,
 * activePowerKw, breakerClosed; BESS passes mode, activePowerKw; meter
 * passes status. All three pass commsOk and lastSeenAt from the merged
 * telemetry payload.
 *
 * `deviceType` gates type-specific running predicates that would otherwise
 * cross-fire across device types — notably `isMeterActive`, where genieos's
 * block-level `'active'` status would falsely promote a stopped generator
 * (zero power, breaker open) into the running tier. See djin-cloud#204
 * follow-up.
 */
export interface DeviceStateInput {
  // Stringified booleans tolerated — the cloud telemetry mapper emits
  // `String(device.faulted ?? false)` for symmetry with breaker_closed.
  // See `coerceBool` below and djin-cloud#209.
  faulted?: boolean | string | number | null;
  status?: string | null;
  commsOk?: boolean | null;
  lastSeenAt?: string | null;
  mode?: string | null;
  // Stringified booleans tolerated — telemetry-mapper.service.ts:242 emits
  // `String(device.breaker_closed ?? false)`. See `coerceBool` below
  // and djin-cloud#214.
  breakerClosed?: boolean | string | number | null;
  activePowerKw?: number | null;
  deviceType?: SLDNodeType;
  /**
   * When true, EVERY fault signal (the `faulted` flag and the `fault` /
   * `faulted` status/mode strings) is ignored and the device is classified on
   * comms + operating state alone. Default false — legacy behavior.
   *
   * Set by VerticalSLD when the device has no ACTIVE (uncleared AND unacked)
   * critical alarm in the cloud. The telemetry fault flag is not an
   * operator-facing fault: genieos raises it for any alarm at/above
   * SeverityUrgent whether or not it has been acknowledged, so an acked-and-open
   * alarm pinned Hondo's BESS card red indefinitely. See djin-cloud#684 and
   * `utils/deviceAlarmSeverity.ts` for the full rationale.
   *
   * This suppresses only the FAULT tier. `offline` / `no_comms` / `unknown` are
   * comms facts, not alarms, and are unaffected.
   */
  faultSuppressed?: boolean;
}

// Stringified booleans arrive from the cloud telemetry mapper for both
// `breaker_closed` and `faulted` (telemetry-mapper.service.ts emits
// `String(device.breaker_closed ?? false)` at :242 and the symmetric
// `String(device.faulted ?? false)` for the faulted flag — see
// djin-cloud#204 and #209). Strict `=== true` would not match the string
// `"true"`, and naive `Boolean(...)` coercion would misclassify the
// literal `"false"` (JS: `Boolean("false") === true`). One helper, used
// at every wire boundary that consumes these stringified booleans.
export function coerceBool(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

/** Freshness-only classification returned by `computeFreshnessTier`. */
export type DeviceFreshnessTier = 'offline' | 'unknown' | 'no_comms' | 'fresh';

/**
 * Freshness-only judgement of whether a device's telemetry can be trusted —
 * deliberately computed WITHOUT ever consulting the fault signal.
 *
 * PRECEDENCE HAZARD this function exists to route around: `computeDeviceState`
 * below evaluates the fault rung (step 1, ~30 lines down) BEFORE this exact
 * same offline -> no_comms -> unknown freshness ladder (its steps 2-3). A
 * truthy `faulted` short-circuits `computeDeviceState` straight to `'fault'`,
 * so its return value tells you NOTHING about staleness whenever the device
 * is also faulted — and genieos's applyAlarmFaultOverride (state_publisher.go
 * :462) sets `faulted=true` for any block carrying a device alarm at or above
 * SeverityUrgent, running or not, comms-alive or not (djin-cloud#622). A
 * faulted generator that has gone dark (commsOk=false, or a lastSeenAt from
 * three days ago, or never seen at all) still reports tier `'fault'` from
 * `computeDeviceState` — which is correct for THAT function's callers
 * (VerticalSLD tile styling, where fault should visually dominate) but wrong
 * for any caller asking "can I trust the data behind this?".
 *
 * Use this function — not `computeDeviceState`'s return value — whenever the
 * question is "is this device's telemetry stale or missing", e.g. the
 * DeviceConditionSections dark-device banner. Do not fold this back into
 * `computeDeviceState`'s ladder or reorder its rungs: other callers
 * (VerticalSLD.tsx) depend on the existing precedence, and
 * `deviceState.test.ts` pins fault-wins-over-offline/no_comms deliberately.
 */
export function computeFreshnessTier(
  input: Pick<DeviceStateInput, 'commsOk' | 'lastSeenAt'>,
  now: Date = new Date(),
): DeviceFreshnessTier {
  // Mirrors computeDeviceState steps 2-3 (offline / no_comms / unknown)
  // below — kept in sync by hand since the two intentionally never share
  // one code path (see hazard note above).
  if (input.commsOk === false) return 'offline';
  if (input.lastSeenAt == null) return 'unknown';
  const lastSeenMs = new Date(input.lastSeenAt).getTime();
  if (Number.isNaN(lastSeenMs)) return 'unknown';
  const ageMs = now.getTime() - lastSeenMs;
  if (ageMs > DEVICE_NO_COMMS_THRESHOLD_MS) return 'no_comms';
  return 'fresh';
}

/**
 * Single source of truth for the SLD device-state tier ladder.
 * Precedence: fault > offline > no_comms > running > standby > unknown.
 * BESS exception: an actively-cycling faulted BESS (deviceType='bess' AND
 * mode in {charging,charge,discharging,discharge} OR |power| > 1) falls
 * through the fault tier to offline / no_comms / running so the card
 * layer can overlay a fault badge on the running visual. See inline
 * comment on the fault tier and the design doc:
 * docs/plans/2026-05-21-sld-bess-fault-and-turner-collapse-design.md §4a.
 *
 * Devices absent from the current SLD telemetry batch arrive at this
 * helper with `lastSeenAt: undefined` (config-only base data, no
 * mapMetricsToNodeData overlay), and fall through to the `unknown`
 * tier — not `offline`. Edge-explicit `commsOk: false` is the only
 * input that still produces `offline`. See djin-cloud#206 for the
 * rationale (backend cold-start false-OFFLINE).
 */
export function computeDeviceState(
  input: DeviceStateInput,
  now: Date = new Date(),
): SLDDeviceState {
  const rawStatus = (input.status ?? '').toLowerCase();
  const rawMode = (input.mode ?? '').toLowerCase();

  // 1. fault — top of the ladder, BUT a BESS that is actively cycling is
  // demoted to 'running' so the tile can render a fault badge over the
  // operational state instead of suppressing all numerics. Rationale: an
  // isolated rack fault that the operator is approved to run through must
  // not look identical to a fully-offline faulted block (Hondo bad-rack
  // case, 2026-05-21). Off/idle/standby/stopped + faulted still returns
  // 'fault' — the existing red FAULT view is preserved for the case it
  // handles correctly. Non-BESS device types keep the original precedence.
  // `faultSuppressed` gates the WHOLE signal — flag and status/mode strings
  // alike — because genieos rewrites the block's display status in the same
  // pass that raises the flag, so suppressing only the flag would leave the
  // string forms still painting the card red. See djin-cloud#684.
  const isFaultSignal =
    !input.faultSuppressed &&
    (coerceBool(input.faulted) ||
      rawStatus === 'fault' ||
      rawMode === 'fault' ||
      rawMode === 'faulted');

  if (isFaultSignal) {
    if (input.deviceType === 'bess') {
      const power = input.activePowerKw ?? 0;
      const isBessActive =
        rawMode === 'charging' ||
        rawMode === 'charge' ||
        rawMode === 'discharging' ||
        rawMode === 'discharge' ||
        Math.abs(power) > 1;
      if (!isBessActive) {
        return 'fault';
      }
      // fall through to the running-tier check below
    } else if (input.deviceType === 'generator') {
      // Mirror of the BESS exception above (djin-cloud#622). genieos's
      // applyAlarmFaultOverride (state_publisher.go:462) sets faulted=true for
      // any block carrying a device alarm at >= SeverityUrgent, whether or not
      // the unit is running — so a generator synchronised and producing 292 kW
      // arrives here faulted. Rendering it identically to a tripped, dead
      // generator (red card, kW blanked to '--') is what #622 fixes: at Hondo it
      // painted 3 of 5 generators FAULT while 4 were running at 64% load.
      //
      // A closed breaker or real power output is producing evidence, so demote
      // to the running tier and let GeneratorCard overlay a fault badge. The
      // fault is NOT discarded — the raw `faulted` prop still reaches the card.
      // A faulted generator with no such evidence (stopped, breaker open, 0 kW)
      // still returns 'fault', which is what keeps GEN-2299 correctly red.
      const power = input.activePowerKw ?? 0;
      const isGenProducing =
        coerceBool(input.breakerClosed) ||
        power > GENERATOR_PRODUCING_THRESHOLD_KW;
      if (!isGenProducing) {
        return 'fault';
      }
      // fall through to the running-tier check below
    } else {
      return 'fault';
    }
  }

  // 2. offline — edge-explicit (commsOk=false from device_online=0)
  // NOTE: steps 1-3's ordering means a faulted device NEVER reaches here —
  // see `computeFreshnessTier` above for the standalone, fault-independent
  // version of steps 2-3 that callers needing freshness (not tile styling)
  // must use instead.
  if (input.commsOk === false) {
    return 'offline';
  }

  // 3. unknown vs no_comms — freshness check
  if (input.lastSeenAt == null) {
    return 'unknown';
  }
  const lastSeenMs = new Date(input.lastSeenAt).getTime();
  if (Number.isNaN(lastSeenMs)) {
    return 'unknown';
  }
  const ageMs = now.getTime() - lastSeenMs;
  if (ageMs > DEVICE_NO_COMMS_THRESHOLD_MS) {
    return 'no_comms';
  }

  // 4. running — any one of these unifies across device types.
  // For generator: status='on'|'running'|'active' is necessary, but a closed
  // breaker (synchronized & tied-out, even at 0 kW dispatch) is sufficient
  // producing evidence — matches the existing demotion logic in
  // GeneratorCard.tsx. genieos emits block-level display status from
  // DeriveBlockDisplayStatus (genieos/internal/site/block_status.go), whose
  // healthy-running vocabulary is "active" — not "on" or "running" — so the
  // tile must accept that string to agree with the detail panel
  // (StatusBadge.isOnlineStatus). See djin-cloud#204.
  // For BESS: 'charge'/'discharge' short aliases mirror existing BESSCard.tsx
  // behavior; the SLD wire today only emits the long forms but the cloud
  // historically accepts both.
  const power = input.activePowerKw ?? 0;
  // 'degraded' is load-bearing, not defensive: DeriveBlockDisplayStatus
  // (genieos internal/site/block_status.go) emits it for a block in state 2
  // with any device offline or faulted, AND applyAlarmFaultOverride rewrites
  // 'active' -> 'degraded' whenever it raises the fault flag. So every
  // generator demoted by the fault-tier branch above arrives here with
  // status='degraded'; without this term they would all fall through to
  // 'standby' and render amber "Standby" with the load % hidden. The
  // corroboration requirement (power or breaker) is unchanged — 'degraded'
  // alone is never sufficient. See djin-cloud#622.
  const isGenRunning =
    (rawStatus === 'on' ||
      rawStatus === 'running' ||
      rawStatus === 'active' ||
      rawStatus === 'degraded') &&
    (power > 0 || coerceBool(input.breakerClosed));
  const isBessRunning =
    rawMode === 'charging' ||
    rawMode === 'charge' ||
    rawMode === 'discharging' ||
    rawMode === 'discharge' ||
    Math.abs(power) > 1;
  // Meters are the only device type where status='active' on its own
  // (no power/breaker corroboration) is sufficient evidence of running.
  // Generator blocks also emit status='active' from
  // DeriveBlockDisplayStatus when state=2 + devices online + no faults —
  // but that means "block controller is on", NOT "device is producing"
  // (a stopped gen with breaker open inside an active block still emits
  // 'active'). Gating on deviceType prevents the cross-fire.
  // See djin-cloud#204 follow-up.
  const isMeterActive =
    input.deviceType === 'meter' && rawStatus === 'active';
  if (isGenRunning || isBessRunning || isMeterActive) {
    // 4b. degraded — running-but-impaired (djin-cloud#618). Producing evidence
    // is established above; `degraded` says the edge ALSO knows something is
    // wrong, so returning 'running' here discards that at the tier boundary.
    //
    // Why this is not redundant with the fault flag: `DeriveBlockDisplayStatus`
    // (genieos internal/site/block_status.go) emits 'degraded' for a block in
    // state 2 with ANY device offline or faulted — strictly broader than
    // `applyAlarmFaultOverride`, which only rewrites 'active' -> 'degraded'
    // when it also raises `faulted`. So a block can arrive here degraded with
    // faulted=false, and GeneratorCard's running-with-fault overlay keys on the
    // raw `faulted` prop (`hasFaultPresent`), not on status. That case rendered
    // a FULLY GREEN "Running" card with no badge and no other indication —
    // the live Turner/Hondo defect this tier exists to close.
    //
    // 🪤 The original #618 report said these render grey "Standby". They did
    // until #622 added 'degraded' to the running vocabulary above; since then
    // the failure direction has been the opposite and worse — indistinguishable
    // from healthy. Both are the same under-statement of an actionable
    // condition, which is why the issue stayed valid while its symptom moved.
    //
    // 🔑 …unless the ONLY reason the edge says 'degraded' is the alarm it also
    // raised the fault flag for, and no active alarm confirms that (#691).
    // genieos writes 'degraded' two ways: `applyAlarmFaultOverride` rewrites
    // 'active' -> 'degraded' in the same pass that sets `faulted=true` (so it
    // is derived from an alarm — one that may well be acknowledged), and
    // `DeriveBlockDisplayStatus` emits it for a block whose devices are offline
    // or faulted, with no alarm involved. `faulted` is what separates them:
    // the first form always carries it, the second usually does not.
    //
    // So suppress ONLY the first form: `faultSuppressed` (no active alarm) AND
    // `faulted` (the flag the rewrite travels with). A degraded block with no
    // fault flag is the #618 case and still renders DEGRADED — that signal is
    // untouched, which matters because it is the only one of the three that
    // fires with no cloud alarm row at all. Hondo, 7 acked-and-open alarms and
    // nothing unacked, was showing amber DEGRADED with an empty alarm panel
    // behind it — the #684 false positive one notch quieter.
    const isAlarmDerivedDegraded = input.faultSuppressed === true && coerceBool(input.faulted);
    if (rawStatus === 'degraded' && !isAlarmDerivedDegraded) return 'degraded';
    return 'running';
  }

  // 5. standby — fall-through (includes generator offline-idle case)
  return 'standby';
}
